import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ShieldAlert, LogOut } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { needsRealEmail } from '../utils/needsRealEmail';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// App-wide blocking gate: a signed-in player whose email is still the internal
// @betohalics.app placeholder (can't receive mail) MUST set a real email before
// they can use the app — so password resets and account notices actually reach them.
export default function ForceRealEmailGate() {
    const { user, refreshUser, logout } = useAuth();
    const [email, setEmail] = useState('');
    const [saving, setSaving] = useState(false);

    // Only customers are forced; ignore unauthenticated and staff/admin accounts.
    if (!needsRealEmail(user)) return null;

    const save = async () => {
        const value = email.trim().toLowerCase();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
            toast.error('Enter a valid email address');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/auth/update-email`, { email: value }, { withCredentials: true });
            toast.success('Email saved — thank you!');
            await refreshUser?.();
            // Hard-refresh once so the gate definitively clears and normal
            // onboarding resumes, regardless of context timing.
            setTimeout(() => { window.location.reload(); }, 400);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save that email');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={() => {}}>
            <DialogContent
                className="bg-[#121212] border-[#FFD700]/40 [&>button]:hidden max-w-md"
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                data-testid="force-email-gate"
            >
                <div className="text-center space-y-3 py-2">
                    <div className="mx-auto w-12 h-12 rounded-full bg-[#FFD700]/15 flex items-center justify-center">
                        <ShieldAlert className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <h2 className="text-xl font-black text-white" data-testid="force-email-title">
                        Add your real email
                    </h2>
                    <p className="text-sm text-[#A0AAB2]">
                        Your account is set up with a placeholder email that <span className="text-white font-semibold">can't receive messages</span>.
                        Add a real email so you can reset a lost password and get important account updates.
                    </p>

                    <div className="relative pt-2">
                        <Mail className="absolute left-3 top-1/2 translate-y-[2px] w-5 h-5 text-[#A0AAB2]" />
                        <Input
                            type="email"
                            inputMode="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                            placeholder="you@email.com"
                            className="pl-10 py-6 text-base bg-[#0a0a0a] border-white/10 text-white placeholder:text-[#A0AAB2]/50 focus:border-[#FFD700] focus:ring-[#FFD700]/20"
                            data-testid="force-email-input"
                        />
                    </div>

                    <Button
                        onClick={save}
                        disabled={saving || !email}
                        className="w-full py-6 bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold text-base"
                        data-testid="force-email-save"
                    >
                        {saving ? 'Saving…' : 'Save my email'}
                    </Button>

                    <button
                        type="button"
                        onClick={() => logout?.()}
                        className="inline-flex items-center gap-1.5 text-xs text-[#A0AAB2] hover:text-white transition-colors"
                        data-testid="force-email-logout"
                    >
                        <LogOut className="w-3.5 h-3.5" /> Sign out instead
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
